import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';
import { useEmailAccounts } from '@/hooks/use-email-accounts';
import { useDebouncedSearchTerm } from '@/hooks/use-debounced-value';
import { isSameQueryScope } from '@/lib/query-scope';
import { useTaskJunctionLink, useTaskJunctionUnlink } from '@/hooks/use-task-junction-mutations';
import { TASK_QUERY_KEYS } from '@/hooks/use-tasks-helpers';

import type { SearchResetRegistration } from '@/hooks/use-debounced-value';
import type { QueryClient } from '@tanstack/react-query';

// =============================================================================
// Types
// =============================================================================

export interface EnrichedLinkedThread {
  thread_id: string;
  subject: string | null;
  from_name: string | null;
  from_email: string | null;
  received_date: string | null;
  snippet: string | null;
  message_count: number;
  created_at: string;
}

export interface ThreadSearchResult {
  thread_id: string;
  subject: string | null;
  from_name: string | null;
  from_email: string | null;
  received_date: string | null;
  snippet: string | null;
  message_count: number;
}

// =============================================================================
// Query Key Factory
// =============================================================================

export const taskLinkedKeys = {
  all: ['task-linked'] as const,
  threads: () => [...taskLinkedKeys.all, 'threads'] as const,
  /**
   * The VIEWER goes LAST, after the task id — deliberately.
   *
   * These rows are viewer-gated, so the key must partition on the viewer. But
   * `removeCorrespondenceAccessCaches` evicts this family by the bare
   * `(taskId)` prefix when a share is revoked (`share-access/task-share-access.ts`),
   * and the junction mutations invalidate by a predicate that looks for the
   * task id in the key. A viewer segment placed BEFORE the task id would break
   * both — silently, and on a security path.
   */
  /**
   * The share-revoke EVICTION prefix — every viewer's copy for this task.
   *
   * Separate from `threadsByTask` on purpose. Calling the full factory with the
   * viewer omitted yields a trailing `undefined` segment, and TanStack compares
   * prefixes element-wise, so `threadsByTask(taskId)` would match only the
   * anonymous viewer's entry and silently leave every real one cached on a
   * revoke. Prefix callers must use this.
   */
  threadsForTask: (taskId: string) => [...taskLinkedKeys.threads(), taskId] as const,
  threadsByTask: (taskId: string, userId: string | undefined) =>
    [...taskLinkedKeys.threadsForTask(taskId), userId] as const,
  /**
   * Every picker search, scoped to the VIEWER as well as the org.
   *
   * The relations behind these searches are viewer-gated, not merely
   * org-gated — `user_can_see_constituent`, `user_can_see_company`, and a
   * correspondence RPC that returns personal-account rows only for
   * `auth.uid()`. An org-only key therefore serves one member's private names,
   * domains and subjects to the next member of the same org out of cache. The
   * viewer sits in this shared segment so all four child keys inherit it and a
   * new picker cannot forget.
   */
  search: (userId: string | undefined) => [...taskLinkedKeys.all, 'search', userId] as const,
  threadSearch: (orgId: string, userId: string | undefined, isPrivate: boolean, query: string) =>
    [...taskLinkedKeys.search(userId), 'threads', orgId, isPrivate, query] as const,
} as const;

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch linked threads for a task with batch enrichment from email_metadata.
 * Uses 2-query pattern to avoid N+1.
 */
export function useTaskLinkedThreads(taskId: string | undefined, orgId: string | undefined) {
  // The viewer is part of the key: these rows are viewer-gated, so an org-only
  // key serves one member's linked names, subjects and snippets to the next.
  const { user } = useAuth();

  return useQuery({
    queryKey: taskLinkedKeys.threadsByTask(taskId ?? '', user?.id),
    queryFn: async (): Promise<EnrichedLinkedThread[]> => {
      if (!taskId || !orgId) {
        return [];
      }

      // Step 1: Fetch junction rows
      const { data: links, error: linksError } = await supabase
        .from('task_linked_threads')
        .select('thread_id, created_at')
        .eq('task_id', taskId);

      if (linksError) {
        console.error('Error fetching linked threads:', linksError);
        throw linksError;
      }

      if (!links || links.length === 0) {
        return [];
      }

      const typedLinks = links as { thread_id: string; created_at: string }[];

      // Step 2: Batch enrichment (1 query instead of N)
      const threadIds = typedLinks.map((l) => l.thread_id);
      const { data: rawEmails, error: emailsError } = await supabase
        .from('email_metadata')
        .select('thread_id, subject, from_name, from_email, received_date, snippet')
        .in('thread_id', threadIds)
        .eq('organization_id', orgId)
        .eq('deleted', false)
        .order('received_date', { ascending: false });

      if (emailsError) {
        console.error('Error enriching linked threads:', emailsError);
        throw emailsError;
      }

      const emails = (rawEmails ?? []) as {
        thread_id: string;
        subject: string | null;
        from_name: string | null;
        from_email: string | null;
        received_date: string | null;
        snippet: string | null;
      }[];

      // Step 3: Group client-side to get latest email + count per thread
      const threadMap = new Map<string, EnrichedLinkedThread>();
      const linkCreatedAt = new Map(typedLinks.map((l) => [l.thread_id, l.created_at]));

      for (const email of emails) {
        const existing = threadMap.get(email.thread_id);
        if (existing) {
          existing.message_count++;
        } else {
          threadMap.set(email.thread_id, {
            thread_id: email.thread_id,
            subject: email.subject,
            from_name: email.from_name,
            from_email: email.from_email,
            received_date: email.received_date,
            snippet: email.snippet,
            message_count: 1,
            created_at: linkCreatedAt.get(email.thread_id) ?? '',
          });
        }
      }

      // Include links with no email_metadata (stale/deleted threads)
      for (const link of typedLinks) {
        if (!threadMap.has(link.thread_id)) {
          threadMap.set(link.thread_id, {
            thread_id: link.thread_id,
            subject: null,
            from_name: null,
            from_email: null,
            received_date: null,
            snippet: null,
            message_count: 0,
            created_at: link.created_at,
          });
        }
      }

      return [...threadMap.values()];
    },
    enabled: !!taskId && !!orgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Search email threads using the FTS RPC function.
 * Filters by account type based on task privacy.
 */
export function useThreadSearch(
  orgId: string | undefined,
  isPrivate: boolean,
  searchTerm: string,
  enabled = true,
  options: SearchResetRegistration = {},
) {
  // The viewer is part of the key: these relations are viewer-gated, so an
  // org-only key serves one member's private rows to the next.
  const { user } = useAuth();
  const { data: accounts = [] } = useEmailAccounts(orgId);
  const debouncedTerm = useDebouncedSearchTerm(searchTerm, 300, options.registerReset);

  // Filter accounts by type based on task privacy
  const filteredAccountIds = accounts
    .filter((a) => a.connection_status === 'connected')
    .filter((a) => (isPrivate ? a.account_type === 'personal' : a.account_type === 'shared'))
    .map((a) => a.id);

  const queryKey = taskLinkedKeys.threadSearch(orgId ?? '', user?.id, isPrivate, debouncedTerm);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<ThreadSearchResult[]> => {
      if (!orgId || filteredAccountIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase.rpc('search_email_threads', {
        p_org_id: orgId,
        p_search_term: debouncedTerm,
        p_account_ids: filteredAccountIds,
        p_limit: 10,
      });

      if (error) {
        console.error('Error searching threads:', error);
        throw error;
      }

      return (data ?? []) as ThreadSearchResult[];
    },
    enabled: enabled && !!orgId && !!user?.id && filteredAccountIds.length > 0,
    placeholderData: (previousData, previousQuery) =>
      // Scoped, not bare `keepPreviousData`: these keys lead with the ORG and
      // the VIEWER, and the rows behind them are gated on both. A placeholder
      // from another scope paints the previous tenant's thread subjects and senders under the new
      // query for the whole in-flight window. Only the trailing search term may
      // change, which it does on every keystroke.
      isSameQueryScope(previousQuery?.queryKey, queryKey) ? previousData : undefined,
    staleTime: 30_000,
    gcTime: 2 * 60 * 1000,
  });
}

const THREAD_JUNCTION_CONFIG = {
  table: 'task_linked_threads' as const,
  itemColumn: 'thread_id',
  invalidationKey: taskLinkedKeys.threads(),
  onMutationSuccess: (queryClient: QueryClient, { itemId }: { itemId: string }) => {
    // Invalidate email-tasks queries that include this threadId
    void queryClient.invalidateQueries({
      queryKey: [TASK_QUERY_KEYS.emailTasks],
      predicate: (query) => Array.isArray(query.queryKey) && query.queryKey.includes(itemId),
      refetchType: 'active',
    });
  },
};

/**
 * Link a thread to a task.
 */
export function useLinkThread() {
  return useTaskJunctionLink(THREAD_JUNCTION_CONFIG);
}

/**
 * Unlink a thread from a task.
 */
export function useUnlinkThread() {
  return useTaskJunctionUnlink(THREAD_JUNCTION_CONFIG);
}
